import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Switch,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { testIds } from '../testIds';

export function EmployerContactFields({
  namePrefix = '',
  requireName,
  showPrimaryToggle,
  primaryToggleDisabled,
}: {
  namePrefix?: string;
  requireName?: boolean;
  showPrimaryToggle?: boolean;
  primaryToggleDisabled?: boolean;
}): React.ReactElement {
  const { register, getFieldState, formState } = useFormContext();

  const firstNameError = getFieldState(`${namePrefix}firstName`, formState).error;
  const lastNameError = getFieldState(`${namePrefix}lastName`, formState).error;
  const emailError = getFieldState(`${namePrefix}email`, formState).error;
  const phoneError = getFieldState(`${namePrefix}phone`, formState).error;

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired={requireName} isInvalid={firstNameError != null}>
          <FormLabel>
            <Trans>First name</Trans>
          </FormLabel>
          <Input
            {...register(`${namePrefix}firstName`)}
            placeholder={t`First name`}
            data-testid={testIds.contactFirstNameInput}
          />
          <FormErrorMessage>{firstNameError?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired={requireName} isInvalid={lastNameError != null}>
          <FormLabel>
            <Trans>Last name</Trans>
          </FormLabel>
          <Input
            {...register(`${namePrefix}lastName`)}
            placeholder={t`Last name`}
            data-testid={testIds.contactLastNameInput}
          />
          <FormErrorMessage>{lastNameError?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={emailError != null}>
          <FormLabel>
            <Trans>Email address</Trans>
          </FormLabel>
          <Input
            {...register(`${namePrefix}email`)}
            type="email"
            placeholder={t`Email address`}
            data-testid={testIds.contactEmailInput}
          />
          <FormErrorMessage>{emailError?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={phoneError != null}>
          <FormLabel>
            <Trans>Phone number</Trans>
          </FormLabel>
          <Input
            {...register(`${namePrefix}phone`)}
            placeholder={t`Phone number`}
            data-testid={testIds.contactPhoneInput}
          />
          <FormErrorMessage>{phoneError?.message}</FormErrorMessage>
        </FormControl>

        <FormControl>
          <FormLabel>
            <Trans>Preferred contact method</Trans>
          </FormLabel>
          <Select
            {...register(`${namePrefix}preferredContactMethod`)}
            placeholder={t`Select method`}
            data-testid={testIds.contactPreferredMethodSelect}
          >
            <option value="EMAIL">{t`Email`}</option>
            <option value="PHONE">{t`Phone`}</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      {showPrimaryToggle ? (
        <FormControl>
          <Switch
            colorScheme="brand"
            {...register(`${namePrefix}isPrimary`)}
            isDisabled={primaryToggleDisabled}
            data-testid={testIds.contactPrimaryToggle}
            display="flex"
            alignItems="center"
          >
            <Trans>Primary contact</Trans>
          </Switch>
          {primaryToggleDisabled ? (
            <FormHelperText>
              <Trans>Promote another contact to change the primary.</Trans>
            </FormHelperText>
          ) : null}
        </FormControl>
      ) : null}
    </>
  );
}
